import {useCallback, useEffect, useMemo, useState} from "react";
import {Link, NavLink, useHistory, useLocation, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../services/use-auth";
import AdvertiseBlock from "../../components/AdvertiseBlock";
import AlphabetFilter from "../../components/AlphabetFilter";
import trackPageView from "../../services/trackPageView";
import usePageMeta from "../../services/page-meta";
import NotFoundPage from "../Errors/NotFound";
import {parseRouteResponse} from "../../services/route-response";

import "../../assets/styles/organizations/organizationView.scss";

const FALLBACK_AVATAR = "/img/no-photo.svg";
const MIN_AVATAR_DIMENSION = 48;
const MIN_AVATAR_RATIO = 0.5;
const MAX_AVATAR_RATIO = 2;

function shouldUseFallbackAvatar(imageElement) {
    const {naturalWidth, naturalHeight} = imageElement;
    if (!naturalWidth || !naturalHeight) {
        return true;
    }

    if (naturalWidth < MIN_AVATAR_DIMENSION || naturalHeight < MIN_AVATAR_DIMENSION) {
        return true;
    }

    const ratio = naturalWidth / naturalHeight;
    return ratio < MIN_AVATAR_RATIO || ratio > MAX_AVATAR_RATIO;
}

function normalizeAvatarOnLoad(event) {
    const image = event.currentTarget;
    if (image.src.endsWith(FALLBACK_AVATAR)) {
        return;
    }

    if (shouldUseFallbackAvatar(image)) {
        image.src = FALLBACK_AVATAR;
    }
}

function normalizeAvatarOnError(event) {
    const image = event.currentTarget;
    if (image.src.endsWith(FALLBACK_AVATAR)) {
        return;
    }

    image.src = FALLBACK_AVATAR;
}

const TAB_PATHS = new Set([
    "",
    "participants",
    "administration",
    "news",
    "vacancies",
    "suborganizations",
    "events",
    "pendingParticipants",
]);

export default function OrganizationView() {
    const {t} = useTranslation();
    const auth = useAuth();
    const history = useHistory();
    const location = useLocation();
    const {urlSlug, tab = ""} = useParams();

    const [organizationData, setOrganizationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joinPending, setJoinPending] = useState(false);
    const [letterFilter, setLetterFilter] = useState("");
    const [notFound, setNotFound] = useState(false);

    const normalizedTab = TAB_PATHS.has(tab) ? tab : "";
    const shouldUseWysiwygStyles = normalizedTab === "";

    usePageMeta({
        title: organizationData?.organization?.name || t('meta.organizationView.title', t('organizations.title')),
        description: t('meta.organizationView.description', t('app.description')),
        keywords: t('meta.organizationView.keywords', t('app.keywords')),
    });

    const loadData = useCallback(() => {
        setLoading(true);
        setNotFound(false);
        fetch(`/api/v1/organization/${urlSlug}`)
            .then(async (res) => {
                const result = await res.json().catch(() => null);
                return {res, result};
            })
            .then(({res, result}) => {
                const {kind, redirectUrl} = parseRouteResponse(res, result);

                if (kind === "not_found") {
                    setOrganizationData(null);
                    setNotFound(true);
                    setLoading(false);
                    return;
                }

                if ((kind === "unauthorized" || kind === "redirect" || kind === "error") && redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                if (kind === "error") {
                    throw new Error(`Failed to load organization: ${res.status}`);
                }

                setOrganizationData(result);
                setLoading(false);
            })
            .catch(() => {
                setOrganizationData(null);
                setLoading(false);
            });
    }, [urlSlug]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (organizationData?.organization?.name) {
            trackPageView(organizationData.organization.name);
        }
    }, [organizationData]);

    useEffect(() => {
        setLetterFilter("");
    }, [normalizedTab, urlSlug]);

    const tabs = useMemo(() => {
        if (!organizationData) {
            return [];
        }

        const details = organizationData.details;

        return [
            {url: "", title: t("organizations.tabs.information"), visible: true},
            {url: "participants", title: t("organizations.tabs.participants"), visible: (details.participants || []).length > 0},
            {url: "administration", title: details.titleVIPTab || t("organizations.tabs.administrations"), visible: (details.administrations || []).length > 0},
            {url: "suborganizations", title: details.titleSubOrganizationTab || t("organizations.tabs.suborganizations"), visible: (details.childCommissions || []).length > 0},
            {url: "events", title: details.titleEventTab || t("organizations.tabs.events"), visible: (details.events || []).length > 0},
            {url: "pendingParticipants", title: t("organizations.tabs.pendingParticipants"), visible: Array.isArray(details.pendingParticipants)},
        ].filter((item) => item.visible);
    }, [organizationData, t]);

    const participants = useMemo(() => {
        if (!organizationData?.details?.participants) {
            return [];
        }

        const sorted = [...organizationData.details.participants].sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));

        if (!letterFilter) {
            return sorted;
        }

        return sorted.filter((item) => (item.lastName || "").toLowerCase().startsWith(letterFilter.toLowerCase()));
    }, [organizationData, letterFilter]);

    const pendingParticipants = useMemo(() => {
        const items = organizationData?.details?.pendingParticipants || [];
        return [...items].sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
    }, [organizationData]);

    const handleJoin = () => {
        if (!organizationData || joinPending) {
            return;
        }

        if (!auth.user) {
            history.push("/account/login", {from: location});
            return;
        }

        if (!auth.user.firstName || !auth.user.lastName) {
            history.push("/profile/complete", {from: location});
            return;
        }

        setJoinPending(true);
        fetch(`/api/v1/organization/${organizationData.organization.id}/join`, {method: "POST"})
            .then(() => loadData())
            .finally(() => setJoinPending(false));
    };

    const renderJoinButton = () => {
        if (!organizationData || organizationData.isAdmin || !organizationData.organization.gatherMembers) {
            return null;
        }

        const status = organizationData.membershipStatus;
        const isDisabled = joinPending || status === "accepted" || status === "pending";
        const text = status === "accepted"
            ? t("organizations.buttons.joined")
            : status === "pending"
                ? t("organizations.buttons.wait")
                : t("organizations.buttons.join");

        return (
            <button className="btn" disabled={isDisabled} onClick={handleJoin}>
                {text}
            </button>
        );
    };

    const renderTitleBlock = (isMobile = false) => (
        <div className={`organization-title-block flex${isMobile ? " mobile" : ""}`}>
            <div className="title">
                <h1>{organizationData.organization.name}</h1>
            </div>
            {organizationData.isAdmin ? (
                <div className="join-block">
                    <button className="btn" onClick={() => (window.location.href = organizationData.adminUrl)}>
                        {t("organizations.buttons.admin")}
                    </button>
                </div>
            ) : (
                <div className="join-block">
                    {renderJoinButton()}
                </div>
            )}
        </div>
    );

    const renderParticipants = (items, isVip, isPendingList = false) => (
        <>
            {!isPendingList && (
                <AlphabetFilter
                    list={organizationData.details.participants || []}
                    contentExtractor={(item) => (item.lastName || "").slice(0, 1).toLowerCase()}
                    notifyChange={setLetterFilter}
                />
            )}
            <div className="organization-person-grid">
                {items.map((person) => (
                    <ParticipantCard
                        key={person.id}
                        person={person}
                        isVip={isVip}
                        onStatusAction={loadData}
                    />
                ))}
            </div>
        </>
    );

    if (loading) {
        return <section><div className="container">Loading...</div></section>;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (!organizationData) {
        return <section><div className="container">Organization not found.</div></section>;
    }

    const {organization, details} = organizationData;

    return (
        <section className="organization-view-page">
            <div className="container">
                <AdvertiseBlock unitId="organizationPage1" blockLocation="top" entityId={organization.id} />

                <div className="back-to-list-navigation">
                    <Link to="/organizations">{t("organizations.backToList")}</Link>
                </div>

                <div className="organization-image" style={{backgroundImage: `url(${organization.coverImage})`}}>
                    {organization.logoImage && (
                        <div className="logo flex-center">
                            <img src={organization.logoImage} alt={organization.name} />
                        </div>
                    )}
                </div>

                <div className="organization-wrap">
                    {renderTitleBlock(true)}

                    <div className="organizations-grid">
                        <div className="content">
                            {renderTitleBlock(false)}

                            <div className="organization-tabs">
                                {tabs.map((item) => (
                                    <NavLink
                                        key={item.url || "root"}
                                        exact={item.url === ""}
                                        to={item.url ? `/organization/${urlSlug}/${item.url}` : `/organization/${urlSlug}`}
                                        activeClassName="active"
                                    >
                                        {item.title}
                                    </NavLink>
                                ))}
                            </div>

                            <div className={`organization-tab-content${shouldUseWysiwygStyles ? " wysiwyg-content" : ""}`}>
                                {normalizedTab === "" && (
                                    <div dangerouslySetInnerHTML={{__html: details.information || ""}} />
                                )}
                                {normalizedTab === "participants" && renderParticipants(participants, details.isVipMember)}
                                {normalizedTab === "administration" && (
                                    <div className="organization-administration-list">
                                        {(details.administrations || []).map((person) => (
                                            <AdministrationCard key={person.id} person={person} />
                                        ))}
                                    </div>
                                )}
                                {normalizedTab === "suborganizations" && (
                                    <ListWithAction
                                        items={details.childCommissions || []}
                                        getTitle={(item) => item.name}
                                        actionLabel={t("organizations.suborganizations.view")}
                                        onAction={(item) => (window.location.href = `/organization/${item.slug}`)}
                                    />
                                )}
                                {normalizedTab === "events" && (
                                    <ListWithAction
                                        items={details.events || []}
                                        getTitle={(item) => item.name}
                                        getSubtitle={(item) => (item.dateStart === item.dateEnd ? item.dateStart : `${item.dateStart} - ${item.dateEnd}`)}
                                        actionLabel={t("organizations.suborganizations.view")}
                                        onAction={(item) => (window.location.href = `/conference/${item.id}`)}
                                    />
                                )}
                                {normalizedTab === "pendingParticipants" && renderParticipants(pendingParticipants, details.isVipMember, true)}
                            </div>

                            <AdvertiseBlock unitId="organizationPage2" blockLocation="bottom" entityId={organization.id} />
                        </div>

                        <aside className="side-block js-scroll-side-block">
                            <div>
                                <div className="contact-info">
                                    <h4>{t("organizations.contactData")}</h4>
                                    {(organization.contacts.phones || []).map((phone) => <p key={phone}>{phone}</p>)}
                                    {(organization.contacts.emails || []).map((email) => <p key={email}><a href={`mailto:${email}`}>{email}</a></p>)}
                                    {(organization.contacts.websites || []).map((url) => <p key={url}><a href={url} target="_blank" rel="noreferrer">{url}</a></p>)}
                                    {(organization.contacts.addresses || []).map((address) => <p key={address}>{address}</p>)}
                                </div>

                                {(organization.galleryImages || []).length > 0 && (
                                    <div className="gallery-block">
                                        <h4>{t("organizations.gallery.caption")}</h4>
                                        <div className="gallery-grid">
                                            {organization.galleryImages.slice(0, 12).map((imageUrl) => (
                                                <div key={imageUrl}>
                                                    <a href={imageUrl} target="_blank" rel="noreferrer">
                                                        <img src={imageUrl} alt="" className="img-fill" />
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                        {organization.galleryImages.length > 12 && (
                                            <a className="btn transparent-btn" href={organization.galleryUrl}>
                                                {t("organizations.gallery.viewAll")}
                                            </a>
                                        )}
                                    </div>
                                )}

                                <AdvertiseBlock unitId="organizationPage3" blockLocation="sidebar" entityId={organization.id} />
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ParticipantCard({person, isVip, onStatusAction}) {
    const avatarStyle = {
        width: "100%",
        height: "100%",
        display: "block",
        objectFit: "cover",
        objectPosition: "center center",
        borderRadius: "50%",
    };

    const acceptMember = () => {
        const formData = new FormData();
        formData.append("memberId[]", person.id);
        fetch(`/api/v1/organization/${person.organizationId}/member/accept-group`, {method: "POST", body: formData})
            .then(onStatusAction);
    };

    const declineMember = () => {
        const formData = new FormData();
        formData.append("memberId[]", person.id);
        fetch(`/api/v1/organization/${person.organizationId}/member/decline-group`, {method: "POST", body: formData})
            .then(onStatusAction);
    };

    return (
        <div className="organization-person-card">
            <div className="photo">
                <img
                    src={person.image || FALLBACK_AVATAR}
                    alt=""
                    onLoad={normalizeAvatarOnLoad}
                    onError={normalizeAvatarOnError}
                    style={avatarStyle}
                />
            </div>
            <div className="content">
                <p className="name">{person.firstName} {person.lastName}</p>
                {person.workPlace && <p>{person.workPlace}</p>}
                {isVip && (
                    <div className="action-block">
                        {person.status === 3 && (
                            <div className="button-block">
                                <button className="add-button" onClick={acceptMember} />
                                <button className="delete-button" onClick={declineMember} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function AdministrationCard({person}) {
    const avatarStyle = {
        width: "100%",
        height: "100%",
        display: "block",
        objectFit: "cover",
        objectPosition: "center center",
        borderRadius: "50%",
    };

    return (
        <div className="organization-administration-card flex-center">
            <div className="photo">
                <img
                    src={person.image || FALLBACK_AVATAR}
                    alt=""
                    onLoad={normalizeAvatarOnLoad}
                    onError={normalizeAvatarOnError}
                    style={avatarStyle}
                />
            </div>
            <div className="content">
                {person.position && <p className="position">{person.position}</p>}
                <p className="name">{person.firstName} {person.lastName}</p>
                {person.description && <p>{person.description}</p>}
            </div>
        </div>
    );
}

function ListWithAction({items, getTitle, getSubtitle, actionLabel, onAction}) {
    return (
        <div className="organization-action-list">
            {items.map((item) => (
                <div className="list-item" key={item.id}>
                    <div>
                        <div className="name">{getTitle(item)}</div>
                        {getSubtitle && <div className="subtitle">{getSubtitle(item)}</div>}
                    </div>
                    <button className="btn" onClick={() => onAction(item)}>
                        {actionLabel}
                    </button>
                </div>
            ))}
        </div>
    );
}
